import { fmtUsd as huUsd, fmtPct as huPct, fmtNum } from '../utils/fmt';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import './TradePlanner.css';
import { openAlertDraft } from '../utils/alerts';
import { tervSzamitas } from '../utils/tradeCalc';

// ── Helpers ───────────────────────────────────────────────────────────────────

function tradeKey() {
  const email = localStorage.getItem('terminal_email') || '';
  return email ? `local_tradelog_${email}` : 'local_tradelog';
}

function settingsKey() {
  const email = localStorage.getItem('terminal_email') || '';
  return email ? `local_settings_${email}` : 'local_settings';
}

function loadTrades() {
  try {
    const key = tradeKey();
    const v = JSON.parse(localStorage.getItem(key));
    if (Array.isArray(v)) return v;
    const old = JSON.parse(localStorage.getItem('tp_trades') ?? '[]');
    if (old.length > 0) localStorage.setItem(key, JSON.stringify(old));
    return old;
  } catch { return []; }
}

function loadSettings() {
  try {
    const v = JSON.parse(localStorage.getItem(settingsKey()));
    if (v && typeof v === 'object') return v;
  } catch {}
  return {
    capital: parseFloat(localStorage.getItem('tp_capital')) || 10000,
    risk:    parseFloat(localStorage.getItem('tp_risk'))    || 1,
  };
}


// Megjelenítés: utils/fmt (magyar formátum)
function fmtUSD(n) {
  if (!isFinite(n)) return '—';
  return huUsd(n, 2);
}

function fmtPct(n, dec = 2) {
  if (!isFinite(n)) return '—';
  return huPct(n, dec);
}

function fmtP(n) {
  if (!isFinite(n) || n === 0) return '—';
  return huUsd(n, n >= 1000 ? 2 : n >= 1 ? 4 : undefined);
}

const n = v => parseFloat(v) || 0;

function calcResult(trade) {
  const { entry, sl, tp1, tp2, tp3, direction, sizeUSD, status } = trade;
  if (!entry || !sizeUSD || status === 'open' || status === 'manual') return null;
  const sign = direction === 'long' ? 1 : -1;
  if (status === 'sl')  return sign * (n(sl)  - n(entry)) / n(entry) * sizeUSD;
  if (status === 'tp1') return sign * (n(tp1) - n(entry)) / n(entry) * sizeUSD;
  if (status === 'tp2') return sign * (n(tp2) - n(entry)) / n(entry) * sizeUSD;
  if (status === 'tp3') return sign * (n(tp3) - n(entry)) / n(entry) * sizeUSD;
  return null;
}

function exitP(t) {
  if (t.status === 'sl')  return n(t.sl);
  if (t.status === 'tp1') return n(t.tp1);
  if (t.status === 'tp2') return n(t.tp2);
  if (t.status === 'tp3') return n(t.tp3);
  return null;
}

function tradeRR(t) {
  const e = n(t.entry), s = n(t.sl), x = exitP(t);
  if (!e || !s || !x) return null;
  const sl = Math.abs(e - s);
  return sl > 0 ? Math.abs(x - e) / sl : null;
}

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'open',   label: 'Nyitott',         cls: 'st-open'   },
  { value: 'tp1',    label: 'TP1 Elért ✓',    cls: 'st-tp1'    },
  { value: 'tp2',    label: 'TP2 Elért ✓',    cls: 'st-tp2'    },
  { value: 'tp3',    label: 'TP3 Elért ✓',    cls: 'st-tp3'    },
  { value: 'sl',     label: 'SL Elért ✗',     cls: 'st-sl'     },
  { value: 'manual', label: 'Manuális zárás',  cls: 'st-manual' },
];

function stCls(s) { return STATUS_OPTIONS.find(o => o.value === s)?.cls ?? ''; }
function stLabel(s) { return STATUS_OPTIONS.find(o => o.value === s)?.label ?? s; }

// ── SVG Price Ladder ──────────────────────────────────────────────────────────

const PAD = { top: 28, bottom: 28, left: 10, right: 135 };

function PriceLadder({ entry, sl, tp1, tp2, tp3, live, direction }) {
  const prices = [entry, sl, tp1, tp2, tp3, live].filter(p => p > 0 && isFinite(p));
  if (prices.length < 2) {
    return (
      <div className="tp-viz-empty">
        Add meg a belépési árat<br />és a stop losst az ár létra<br />megjelenítéséhez
      </div>
    );
  }

  const visMin = Math.min(...prices) * 0.998;
  const visMax = Math.max(...prices) * 1.002;
  const range  = visMax - visMin || 1;
  const W = 300, H = 420;
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top  - PAD.bottom;
  const toY = p => PAD.top + (visMax - p) / range * cH;
  const entryY = toY(entry);
  const slY    = sl   > 0 ? toY(sl)   : null;
  const tp1Y   = tp1  > 0 ? toY(tp1)  : null;
  const tp2Y   = tp2  > 0 ? toY(tp2)  : null;
  const tp3Y   = tp3  > 0 ? toY(tp3)  : null;
  const liveY  = live > 0 ? toY(live) : null;
  const lx = PAD.left, rx = PAD.left + cW;

  const zones = [];
  if (sl > 0 && slY !== null) {
    const isLong = direction === 'long';
    const top = Math.min(isLong ? slY : entryY, isLong ? entryY : slY);
    const ht  = Math.abs(slY - entryY);
    zones.push(<rect key="sl-z" x={lx} y={top} width={cW} height={ht} fill="rgba(255,77,77,0.06)" />);
  }
  [[tp1Y, 'rgba(76,175,125,0.06)'], [tp2Y, 'rgba(76,175,125,0.09)'], [tp3Y, 'rgba(76,175,125,0.12)']].forEach(([y, fill], i) => {
    if (!y) return;
    const prevY = i === 0 ? entryY : [tp1Y, tp2Y, tp3Y][i - 1];
    if (!prevY) return;
    const isLong = direction === 'long';
    const top = Math.min(isLong ? y : prevY, isLong ? prevY : y);
    zones.push(<rect key={`tp-z-${i}`} x={lx} y={top} width={cW} height={Math.abs(y - prevY)} fill={fill} />);
  });

  function line({ y, color, dash, label, price, distPct }) {
    if (y === null) return null;
    const lX = rx + 8;
    return (
      <g key={label}>
        {dash
          ? <line x1={lx} y1={y} x2={rx} y2={y} stroke={color} strokeWidth="1.5" strokeDasharray="5,3" />
          : <line x1={lx} y1={y} x2={rx} y2={y} stroke={color} strokeWidth="2" />
        }
        <text x={lX} y={y - 8} fill={color} fontSize="9" fontWeight="700" fontFamily="monospace" letterSpacing="0.04em">{label}</text>
        <text x={lX} y={y + 2} fill={color} fontSize="10" fontFamily="monospace" fontVariantNumeric="tabular-nums">{fmtP(price)}</text>
        {distPct !== undefined && (
          <text x={lX} y={y + 13} fill={color} fontSize="9" opacity="0.7" fontFamily="monospace">{fmtPct(distPct)}</text>
        )}
      </g>
    );
  }

  const dp = (price) => entry > 0 ? (price - entry) / entry * 100 : undefined;

  const tickCount = 5;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => ({
    price: visMin + range * i / tickCount,
    y: toY(visMin + range * i / tickCount),
  }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="tp-ladder-svg" preserveAspectRatio="xMidYMid meet">
      {ticks.map(({ y }, i) => <line key={i} x1={lx} y1={y} x2={rx} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />)}
      {zones}
      {slY !== null && (
        <rect x={lx} y={Math.min(slY, entryY)} width="4" height={Math.abs(slY - entryY)} fill="rgba(255,77,77,0.5)" rx="2" />
      )}
      {[tp1Y, tp2Y, tp3Y].map((y, i) => {
        if (!y) return null;
        const prev = i === 0 ? entryY : [tp1Y, tp2Y, tp3Y][i - 1];
        if (!prev) return null;
        return <rect key={i} x={lx} y={Math.min(y, prev)} width="4" height={Math.abs(y - prev)} fill="rgba(76,175,125,0.5)" rx="2" />;
      })}
      {line({ y: slY,    color: '#ff4d4d', dash: true,  label: 'SL',    price: sl,  distPct: dp(sl)  })}
      {line({ y: tp3Y,   color: '#1a8c55', dash: false, label: 'TP3',   price: tp3, distPct: dp(tp3) })}
      {line({ y: tp2Y,   color: '#2db36c', dash: false, label: 'TP2',   price: tp2, distPct: dp(tp2) })}
      {line({ y: tp1Y,   color: '#4caf7d', dash: false, label: 'TP1',   price: tp1, distPct: dp(tp1) })}
      {line({ y: entryY, color: '#4a90e2', dash: false, label: 'BELÉPŐ', price: entry })}
      {liveY !== null && line({ y: liveY, color: '#4da6ff', dash: true, label: 'ÉLŐ', price: live, distPct: dp(live) })}
    </svg>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TradePlanner({ coins, selectedCoin, onSelectCoin }) {

  // ── Coin selector ──────────────────────────────────────────────────────────
  // One selected coin for the whole terminal (App.selectedCoin): the dropdown
  // here switches it, and a watchlist / search pick switches this tab too.
  const [localCoinId, setLocalCoinId] = useState(selectedCoin?.id ?? coins?.[0]?.id ?? '');
  const calcCoinId = onSelectCoin ? (selectedCoin?.id ?? coins?.[0]?.id ?? '') : localCoinId;
  const setCalcCoinId = (id) => {
    if (!onSelectCoin) { setLocalCoinId(id); return; }
    const c = coins?.find(x => x.id === id);
    if (c) onSelectCoin(c);
  };
  const calcCoin  = useMemo(() => coins?.find(c => c.id === calcCoinId) ?? selectedCoin ?? null, [coins, calcCoinId, selectedCoin]);
  const livePrice = calcCoin?.current_price ?? null;
  const coinSym   = calcCoin?.symbol?.toUpperCase() ?? '?';
  const prevCoinRef = useRef(null);

  // ── Account ────────────────────────────────────────────────────────────────
  const [capital, setCapital] = useState(() => loadSettings().capital ?? 10000);
  const [riskPct, setRiskPct] = useState(() => loadSettings().risk    ?? 1);
  const [leverageMode, setLeverageMode] = useState('risk'); // 'risk' | 'leverage'
  const [manualLeverage, setManualLeverage] = useState('10');

  // ── Planner ────────────────────────────────────────────────────────────────
  const [direction, setDirection] = useState('long');
  const [entry, setEntry] = useState('');
  const [sl,    setSl]    = useState('');
  const [tp1,   setTp1]   = useState('');
  const [tp2,   setTp2]   = useState('');
  const [tp3,   setTp3]   = useState('');
  const [tp1Pct, setTp1Pct] = useState('33');
  const [tp2Pct, setTp2Pct] = useState('33');
  const [tp3Pct, setTp3Pct] = useState('34');

  // ── Log ────────────────────────────────────────────────────────────────────
  const [trades,    setTrades]    = useState(loadTrades);
  const [sortField, setSortField] = useState('id');
  const [sortAsc,   setSortAsc]   = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  // Ref to always access latest trades in callbacks without stale closure
  const tradesRef = useRef(trades);
  useEffect(() => { tradesRef.current = trades; }, [trades]);

  // Auto-fill entry from coin selector (only when coin changes, not on price refresh)
  useEffect(() => {
    if (calcCoin?.id !== prevCoinRef.current) {
      prevCoinRef.current = calcCoin?.id;
      if (livePrice != null) setEntry(String(livePrice));
    }
  }, [calcCoin?.id, livePrice]);

  // Persist settings and trades locally
  useEffect(() => {
    localStorage.setItem(settingsKey(), JSON.stringify({ capital, risk: riskPct }));
  }, [capital, riskPct]);
  useEffect(() => { localStorage.setItem(tradeKey(), JSON.stringify(trades)); }, [trades]);

  // ── Core calculations ──────────────────────────────────────────────────────

  const entryN     = n(entry);
  const slN        = n(sl);
  const manualLev  = parseFloat(manualLeverage) || 1;
  // a terv számai egy tiszta függvényből (utils/tradeCalc.js); tőkeáttétel-módban a Max Veszteség
  // a pozícióméret × stoptávolság (Terminál-javítások 1. kör, A.1)
  const { maxRisk, slDist, posSizeUSD, leverage, maxLoss, maxLossPctOfCapital } = tervSzamitas({
    capital, riskPct, entry: entryN, sl: slN, leverageMode, manualLev,
  });

  const calc1 = useMemo(() => {
    const tpN = n(tp1), tpP = n(tp1Pct);
    if (!entryN || !slN || !tpN || slDist === 0) return { rr: 0, be: 0, profit: 0 };
    const sign = direction === 'long' ? 1 : -1;
    const tpDist = Math.abs(tpN - entryN) / entryN;
    const rr = tpDist / slDist;
    return { rr, be: rr > 0 ? 100 / (1 + rr) : 0, profit: sign * (tpN - entryN) / entryN * posSizeUSD * (tpP / 100) };
  }, [tp1, tp1Pct, entryN, slN, slDist, direction, posSizeUSD]);

  const calc2 = useMemo(() => {
    const tpN = n(tp2), tpP = n(tp2Pct);
    if (!entryN || !slN || !tpN || slDist === 0) return { rr: 0, be: 0, profit: 0 };
    const sign = direction === 'long' ? 1 : -1;
    const tpDist = Math.abs(tpN - entryN) / entryN;
    const rr = tpDist / slDist;
    return { rr, be: rr > 0 ? 100 / (1 + rr) : 0, profit: sign * (tpN - entryN) / entryN * posSizeUSD * (tpP / 100) };
  }, [tp2, tp2Pct, entryN, slN, slDist, direction, posSizeUSD]);

  const calc3 = useMemo(() => {
    const tpN = n(tp3), tpP = n(tp3Pct);
    if (!entryN || !slN || !tpN || slDist === 0) return { rr: 0, be: 0, profit: 0 };
    const sign = direction === 'long' ? 1 : -1;
    const tpDist = Math.abs(tpN - entryN) / entryN;
    const rr = tpDist / slDist;
    return { rr, be: rr > 0 ? 100 / (1 + rr) : 0, profit: sign * (tpN - entryN) / entryN * posSizeUSD * (tpP / 100) };
  }, [tp3, tp3Pct, entryN, slN, slDist, direction, posSizeUSD]);

  // ── Stats ──────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const closed = trades.filter(t => t.status !== 'open' && t.status !== 'manual');
    const wins   = closed.filter(t => ['tp1','tp2','tp3'].includes(t.status));
    const pnls   = trades.map(calcResult).filter(r => r !== null);
    const totalPnL = pnls.reduce((a, b) => a + b, 0);
    const rrVals = closed.map(tradeRR).filter(r => r != null && r > 0);
    const avgRR  = rrVals.length > 0 ? rrVals.reduce((a, b) => a + b, 0) / rrVals.length : 0;
    return {
      total:    trades.length,
      open:     trades.filter(t => t.status === 'open').length,
      winRate:  closed.length > 0 ? wins.length / closed.length * 100 : 0,
      totalPnL,
      avgRR,
      best:     pnls.length > 0 ? Math.max(...pnls) : 0,
      worst:    pnls.length > 0 ? Math.min(...pnls) : 0,
    };
  }, [trades]);

  // ── Sorted trades ──────────────────────────────────────────────────────────

  const sortedTrades = useMemo(() => {
    return [...trades].sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (sortField === 'result') { va = calcResult(a) ?? -Infinity; vb = calcResult(b) ?? -Infinity; }
      if (sortField === 'rr')    { va = tradeRR(a) ?? -1; vb = tradeRR(b) ?? -1; }
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortAsc ? cmp : -cmp;
    });
  }, [trades, sortField, sortAsc]);

  function handleSort(field) {
    setSortAsc(prev => sortField === field ? !prev : false);
    setSortField(field);
  }

  function SortTh({ field, label, cls }) {
    const active = sortField === field;
    return (
      <th className={`tp-th-sort ${cls ?? ''} ${active ? 'active' : ''}`} onClick={() => handleSort(field)}>
        {label} {active ? (sortAsc ? '▲' : '▼') : ''}
      </th>
    );
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleLogTrade = useCallback(() => {
    if (!entry || !sl) return;
    const newTrade = {
      id:        Date.now(),
      date:      new Date().toLocaleDateString('hu-HU'),
      coinSym,
      direction,
      entry, sl, tp1, tp2, tp3,
      tp1Pct, tp2Pct, tp3Pct,
      sizeUSD:  posSizeUSD,
      leverage,
      status:   'open',
      notes:    '',
    };
    setTrades(prev => [newTrade, ...prev]);
  }, [entry, sl, tp1, tp2, tp3, tp1Pct, tp2Pct, tp3Pct, direction, posSizeUSD, leverage, coinSym]);

  const handleSetStatus = useCallback((id, newStatus) => {
    setTrades(prev => prev.map(t => t.id === id ? { ...t, status: newStatus } : t));
  }, []);

  const handleUpdateNote = useCallback((id, notes) => {
    setTrades(prev => prev.map(t => t.id === id ? { ...t, notes } : t));
  }, []);

  const handleDelete = useCallback((id) => {
    setTrades(prev => prev.filter(t => t.id !== id));
  }, []);

  const handleExport = useCallback(() => {
    const rows = trades.map(t => {
      const res = calcResult(t);
      return [
        t.date, t.coinSym, t.direction, t.entry, t.sl,
        t.tp1 || '', t.tp2 || '', t.tp3 || '',
        t.sizeUSD?.toFixed(2), t.leverage?.toFixed(2),
        t.status, res != null ? res.toFixed(2) : '',
        `"${(t.notes || '').replace(/"/g, '""')}"`,
      ].join(',');
    });
    const blob = new Blob(['Dátum,Coin,Irány,Entry,SL,TP1,TP2,TP3,Méret($),Tőkeáttétel,Státusz,Eredmény($),Megjegyzés\n' + rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: 'trade-naplo.csv' }).click();
    URL.revokeObjectURL(url);
  }, [trades]);

  const fieldPct = v => {
    if (!entryN || !n(v)) return '';
    const p = (n(v) - entryN) / entryN * 100;
    return `${fmtPct(p, 2)}`;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="tp-root">

      {/* ── TOP BAR ── */}
      <div className="tp-topbar">
        {/* Coin selector */}
        {coins?.length > 0 && (
          <div className="tp-topbar-group">
            <span className="tp-topbar-label">Coin</span>
            <select
              className="tp-coin-select"
              value={calcCoinId}
              onChange={e => setCalcCoinId(e.target.value)}
            >
              {coins.map(c => (
                <option key={c.id} value={c.id}>{c.symbol.toUpperCase()} — {c.name}</option>
              ))}
            </select>
            {livePrice != null && (
              <span className="tp-live-chip">● {fmtP(livePrice)}</span>
            )}
          </div>
        )}

        <div className="tp-topbar-group">
          <span className="tp-topbar-label">Számla Tőke</span>
          <div className="tp-topbar-input-wrap">
            <span className="tp-topbar-pre">$</span>
            <input className="tp-topbar-input" type="number" min="0" step="100"
              value={capital} onChange={e => setCapital(parseFloat(e.target.value) || 0)} />
          </div>
        </div>

        {leverageMode === 'leverage' ? (
          <div className="tp-topbar-group">
            <span className="tp-topbar-label">Tőkeáttétel</span>
            <div className="tp-topbar-input-wrap">
              <input className="tp-topbar-input" type="number" min="1" max="100" step="1"
                value={manualLeverage} onChange={e => setManualLeverage(e.target.value)} />
              <span className="tp-topbar-suf">×</span>
            </div>
          </div>
        ) : (
          <div className="tp-topbar-group">
            <span className="tp-topbar-label">Kockázat/Trade</span>
            <div className="tp-topbar-input-wrap">
              <input className="tp-topbar-input" type="number" min="0.1" max="100" step="0.1"
                value={riskPct} onChange={e => setRiskPct(parseFloat(e.target.value) || 1)} />
              <span className="tp-topbar-suf">%</span>
            </div>
          </div>
        )}

        {/* tőkeáttétel-módban a kockázat% nem számít: a „Max kockázat” ott ellentmondana a lenti Max Veszteségnek */}
        {leverageMode === 'risk' && (
          <div className="tp-topbar-derived">
            <div className="tp-topbar-derived-val">Max kockázat: <strong>{fmtUSD(maxRisk)}</strong></div>
            <div className="tp-topbar-derived-sub">Számla tőke × kockázat = max veszteség / trade</div>
          </div>
        )}

        <div className="tp-topbar-sync">💾 Helyi mentés</div>
      </div>

      {/* ── STATS ROW ── */}
      <div className="tp-stats-bar">
        {[
          { label: 'Összes Trade',     val: stats.total,   sub: `${stats.open} nyitott` },
          { label: 'Nyerési Arány',    val: stats.winRate > 0 ? `${fmtNum(stats.winRate, 0)}%` : '—', cls: stats.winRate >= 50 ? 'pos' : '', sub: 'zártak alapján' },
          { label: 'Teljes P&L',       val: trades.length > 0 ? fmtUSD(stats.totalPnL) : '—', cls: stats.totalPnL > 0 ? 'pos' : stats.totalPnL < 0 ? 'neg' : '', sub: 'realizált' },
          { label: 'Átl. R:R',         val: stats.avgRR > 0 ? fmtNum(stats.avgRR, 2) : '—', sub: 'zárt trade-ek' },
          { label: 'Legjobb Trade',    val: stats.best > 0 ? fmtUSD(stats.best) : '—', cls: 'pos' },
          { label: 'Legrosszabb Trade', val: stats.worst < 0 ? fmtUSD(stats.worst) : '—', cls: stats.worst < 0 ? 'neg' : '' },
        ].map(({ label, val, sub, cls }) => (
          <div key={label} className="tp-stat">
            <div className="tp-stat-label">{label}</div>
            <div className={`tp-stat-value ${cls === 'pos' ? 'tp-pos' : cls === 'neg' ? 'tp-neg' : ''}`}>{val}</div>
            {sub && <div className="tp-stat-sub">{sub}</div>}
          </div>
        ))}
      </div>

      {/* ── THREE-COLUMN MAIN ── */}
      <div className="tp-main-grid">

        {/* ── LEFT: Pozíció Tervező ── */}
        <div className="tp-col-left">
          <div className="tp-panel-title">POZÍCIÓ TERVEZŐ</div>

          {/* Leverage mode toggle */}
          <div className="tp-mode-toggle">
            <button
              className={`tp-mode-btn ${leverageMode === 'risk' ? 'active' : ''}`}
              onClick={() => setLeverageMode('risk')}
            >
              📐 Kockázat alapú
            </button>
            <button
              className={`tp-mode-btn ${leverageMode === 'leverage' ? 'active' : ''}`}
              onClick={() => setLeverageMode('leverage')}
            >
              ⚡ Tőkeáttétel alapú
            </button>
          </div>

          {/* Direction */}
          <div className="tp-dir-group">
            <button className={`tp-dir-btn long  ${direction === 'long'  ? 'active' : ''}`}
              onClick={() => setDirection('long')}>▲ LONG</button>
            <button className={`tp-dir-btn short ${direction === 'short' ? 'active' : ''}`}
              onClick={() => setDirection('short')}>▼ SHORT</button>
          </div>

          {/* Price inputs */}
          <div className="tp-fields">
            <div className="tp-field">
              <span className="tp-field-label">Belépési Ár</span>
              <input className="tp-field-input entry" type="number" placeholder="0.00"
                value={entry} onChange={e => setEntry(e.target.value)} />
              <span className="tp-field-badge">—</span>
            </div>
            <div className="tp-field">
              <span className="tp-field-label">Stop Loss</span>
              <input className="tp-field-input sl" type="number" placeholder="0.00"
                value={sl} onChange={e => setSl(e.target.value)} />
              <span className="tp-field-badge">{fieldPct(sl)}</span>
            </div>
          </div>

          {/* Live derived stats */}
          <div className="tp-derived-grid">
            <div className="tp-derived-item">
              <div className="tp-derived-label">SL Távolság</div>
              <div className="tp-derived-value">{slDist > 0 ? `${fmtNum(slDist * 100, 2)}%` : '—'}</div>
            </div>
            <div className="tp-derived-item">
              <div className="tp-derived-label">Pozíció Méret</div>
              <div className="tp-derived-value">{posSizeUSD > 0 ? fmtUSD(posSizeUSD) : '—'}</div>
            </div>
            <div className="tp-derived-item">
              <div className="tp-derived-label">Megvásárolható {coinSym}</div>
              <div className="tp-derived-value">
                {posSizeUSD > 0 && livePrice > 0
                  ? `${fmtNum(posSizeUSD / livePrice, 6)} ${coinSym}`
                  : '—'}
              </div>
            </div>
            <div className="tp-derived-item">
              <div className="tp-derived-label">Max Veszteség</div>
              <div className="tp-derived-value tp-neg">{slDist > 0 ? fmtUSD(-maxLoss) : '—'}</div>
              {leverageMode === 'leverage' && maxLossPctOfCapital != null && (
                <div className="tp-derived-sub">a tőkéd {fmtNum(maxLossPctOfCapital, 1)}%-a</div>
              )}
            </div>
            <div className="tp-derived-item">
              <div className="tp-derived-label">
                Tőkeáttétel
                <span className="tp-lev-badge">
                  {leverageMode === 'risk' ? '🔒 Számított' : '✏️ Manuális'}
                </span>
              </div>
              <div className="tp-derived-value">{leverage > 0 ? `${fmtNum(leverage, 1)}×` : '—'}</div>
            </div>
          </div>

          {/* TP levels */}
          <div className="tp-tp-section">
            <div className="tp-tp-hdr">
              <span>Célár</span><span>Ár</span><span>R:R</span><span>B/E%</span><span>Profit</span>
            </div>
            {[
              { label: 'TP1', val: tp1, setVal: setTp1, pct: tp1Pct, setPct: setTp1Pct, c: calc1 },
              { label: 'TP2', val: tp2, setVal: setTp2, pct: tp2Pct, setPct: setTp2Pct, c: calc2 },
              { label: 'TP3', val: tp3, setVal: setTp3, pct: tp3Pct, setPct: setTp3Pct, c: calc3 },
            ].map(({ label, val, setVal, c }) => (
              <div key={label} className="tp-tp-row">
                <span className="tp-tp-lbl">{label}</span>
                <input className="tp-field-input tp" type="number" placeholder="0.00"
                  value={val} onChange={e => setVal(e.target.value)} />
                <span className="tp-tp-rr">{c.rr > 0 ? `${fmtNum(c.rr, 1)}R` : '—'}</span>
                <span className="tp-tp-be">{c.be > 0 ? `${fmtNum(c.be, 0)}%` : '—'}</span>
                <span className="tp-tp-profit">{c.profit !== 0 ? fmtUSD(c.profit) : '—'}</span>
              </div>
            ))}
          </div>

          <button className="tp-log-btn" onClick={handleLogTrade} disabled={!entry || !sl}>
            📋 Hozzáadás a Naplóhoz
          </button>
          {/* One click: entry, stop and target become three plan-level alerts
              (editable preview in the Riasztások panel). */}
          <button
            type="button"
            className="tp-alert-btn"
            disabled={!entry && !sl && !tp1}
            onClick={() => openAlertDraft({
              kind: 'plan', source: 'planner', coin: calcCoin,
              name: `${coinSym} ${direction}`, direction, entry, sl, tp: tp1,
            })}
          >
            🔔 Riasztások a tervhez
          </button>
        </div>

        {/* ── CENTER: Ár Létra ── */}
        <div className="tp-col-center">
          <div className="tp-panel-title">ÁR LÉTRA</div>
          {livePrice != null && (
            <div className="tp-ladder-live-badge">
              <span className="tp-live-dot">●</span> Élő ár: {fmtP(livePrice)}
            </div>
          )}
          <div className="tp-ladder-wrap">
            <PriceLadder
              entry={n(entry)} sl={n(sl)}
              tp1={n(tp1)} tp2={n(tp2)} tp3={n(tp3)}
              live={livePrice ?? 0}
              direction={direction}
            />
          </div>
        </div>

        {/* ── RIGHT: Trade Napló Sidebar ── */}
        <div className="tp-col-right">
          <div className="tp-log-sidebar-header">
            <span className="tp-panel-title">TRADE NAPLÓ</span>
            <span className="tp-log-count">{stats.total} trade</span>
          </div>

          {/* Compact stats 2×3 */}
          <div className="tp-log-mini-stats">
            {[
              { label: 'Összes',    val: stats.total },
              { label: 'Win %',     val: stats.winRate > 0 ? `${fmtNum(stats.winRate, 0)}%` : '—', cls: stats.winRate >= 50 ? 'tp-pos' : '' },
              { label: 'P&L',       val: trades.length ? fmtUSD(stats.totalPnL) : '—', cls: stats.totalPnL > 0 ? 'tp-pos' : stats.totalPnL < 0 ? 'tp-neg' : '' },
              { label: 'Átl. R:R', val: stats.avgRR > 0 ? fmtNum(stats.avgRR, 2) : '—' },
              { label: 'Legjobb',   val: stats.best > 0 ? fmtUSD(stats.best) : '—', cls: 'tp-pos' },
              { label: 'Legrosszabb', val: stats.worst < 0 ? fmtUSD(stats.worst) : '—', cls: 'tp-neg' },
            ].map(({ label, val, cls }) => (
              <div key={label} className="tp-mini-stat">
                <div className="tp-mini-label">{label}</div>
                <div className={`tp-mini-val ${cls ?? ''}`}>{val}</div>
              </div>
            ))}
          </div>

          {/* Compact trade list */}
          <div className="tp-compact-list">
            {trades.length === 0 ? (
              <div className="tp-compact-empty">Nincs naplózott trade.</div>
            ) : (
              trades.map(t => {
                const res = calcResult(t);
                const xp  = exitP(t);
                return (
                  <div key={t.id} className={`tp-compact-row tp-cr-${t.status}`}>
                    <div className="tp-cr-left">
                      <span className="tp-cr-sym">{t.coinSym ?? '?'}</span>
                      <span className={`tp-cr-dir ${t.direction === 'long' ? 'tp-pos' : 'tp-neg'}`}>
                        {t.direction === 'long' ? '▲' : '▼'} {t.direction.toUpperCase()}
                      </span>
                    </div>
                    <div className="tp-cr-mid">
                      <span className="tp-cr-price">{fmtP(n(t.entry))}</span>
                      {xp && <><span className="tp-cr-arrow">→</span><span className="tp-cr-price">{fmtP(xp)}</span></>}
                    </div>
                    <div className="tp-cr-right">
                      <span className={`tp-cr-status ${stCls(t.status)}`}>{stLabel(t.status)}</span>
                      {res != null && (
                        <span className={`tp-cr-pnl ${res >= 0 ? 'tp-pos' : 'tp-neg'}`}>{fmtUSD(res)}</span>
                      )}
                    </div>
                    <button className="tp-cr-del" onClick={() => handleDelete(t.id)}>✕</button>
                  </div>
                );
              })
            )}
          </div>

          {/* Sidebar footer */}
          <div className="tp-log-sidebar-footer">
            {trades.length > 0 && (
              <>
                <button className="tp-export-btn" onClick={handleExport}>📥 CSV exportálás</button>
                {confirmClear ? (
                  <div className="tp-confirm-row">
                    <span>Biztos?</span>
                    <button className="tp-confirm-yes" onClick={() => { setTrades([]); setConfirmClear(false); }}>Igen</button>
                    <button className="tp-confirm-no"  onClick={() => setConfirmClear(false)}>Nem</button>
                  </div>
                ) : (
                  <button className="tp-clear-btn" onClick={() => setConfirmClear(true)}>🗑 Összes törlése</button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── FULL WIDTH TABLE ── */}
      <div className="tp-log-section">
        <div className="tp-log-header">
          <span className="tp-panel-title">RÉSZLETES TRADE NAPLÓ</span>
          {trades.length > 0 && (
            <span className="tp-log-count">{trades.length} bejegyzés</span>
          )}
        </div>

        {trades.length === 0 ? (
          <div className="tp-log-empty-state">
            <div className="tp-log-empty-icon">📋</div>
            <div className="tp-log-empty-msg">
              Még nincs naplózott trade.<br />
              Tölts ki egy pozíciót és kattints a <strong>Hozzáadás a Naplóhoz</strong> gombra.
            </div>
          </div>
        ) : (
          <div className="tp-log-table-wrap">
            <table className="tp-log-table">
              <thead>
                <tr>
                  <SortTh field="date"      label="Dátum" />
                  <SortTh field="coinSym"   label="Coin" />
                  <SortTh field="direction" label="Irány" />
                  <SortTh field="entry"     label="Entry" cls="right" />
                  <th className="right">SL</th>
                  <th className="right">TP1</th>
                  <th className="right">TP2</th>
                  <th className="right">TP3</th>
                  <SortTh field="sizeUSD"   label="Méret" cls="right" />
                  <SortTh field="leverage"  label="Lev." cls="right" />
                  <th>Státusz</th>
                  <SortTh field="rr"     label="R:R"      cls="right" />
                  <SortTh field="result" label="Eredmény" cls="right" />
                  <th>Megjegyzés</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedTrades.map(t => {
                  const result = calcResult(t);
                  const rr     = tradeRR(t);
                  const rowCls = ['tp1','tp2','tp3'].includes(t.status) ? 'tp-row-win'
                    : t.status === 'sl'   ? 'tp-row-loss'
                    : t.status === 'open' ? 'tp-row-open'
                    : '';
                  return (
                    <tr key={t.id} className={rowCls}>
                      <td>{t.date}</td>
                      <td><span className="tp-tbl-sym">{t.coinSym ?? '?'}</span></td>
                      <td className={t.direction === 'long' ? 'tp-pos tp-fw' : 'tp-neg tp-fw'}>
                        {t.direction === 'long' ? '▲ LONG' : '▼ SHORT'}
                      </td>
                      <td className="right">{fmtP(n(t.entry))}</td>
                      <td className="right tp-neg">{fmtP(n(t.sl))}</td>
                      <td className="right">{t.tp1 ? fmtP(n(t.tp1)) : '—'}</td>
                      <td className="right">{t.tp2 ? fmtP(n(t.tp2)) : '—'}</td>
                      <td className="right">{t.tp3 ? fmtP(n(t.tp3)) : '—'}</td>
                      <td className="right">{fmtUSD(t.sizeUSD)}</td>
                      <td className="right">{t.leverage != null ? fmtNum(t.leverage, 1) : '—'}×</td>
                      <td>
                        <select
                          className={`tp-status-select ${stCls(t.status)}`}
                          value={t.status}
                          onChange={e => handleSetStatus(t.id, e.target.value)}
                        >
                          {STATUS_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="right">{rr != null ? `${fmtNum(rr, 2)}R` : '—'}</td>
                      <td className={`right ${result > 0 ? 'tp-pos' : result < 0 ? 'tp-neg' : ''}`}>
                        <strong>{result != null ? fmtUSD(result) : '—'}</strong>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="tp-note-input"
                          value={t.notes ?? ''}
                          onChange={e => handleUpdateNote(t.id, e.target.value)}
                          placeholder="Megjegyzés…"
                        />
                      </td>
                      <td>
                        <button className="tp-delete-btn" onClick={() => handleDelete(t.id)}>×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
